import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { UAParser } from "ua-parser-js";
import ReCAPTCHA from "react-google-recaptcha";
import {
  ANONYMOUS_SESSION_ENTITY_ID_KEY,
  getStorageItemWithFallback,
  saveItemToStorage,
} from "@jugl-web/utils/storage";
import { getUniqueId } from "@jugl-web/utils";
import api from "@web-src/features/api/api";
import { selectAuthState } from "@web-src/features/auth/authSlice";
import { AnonymousSession } from "@jugl-web/rest-api/ai-workflows/types";
import { getJwtToken } from "@web-src/features/auth/utils";
import { logger } from "@web-src/utils/logger";
import { environment } from "@web-src/environments/environment";
import { AxiosError } from "axios";
import {
  ANONYMOUS_SESSION_FALLBACK,
  AnonymousSessionContext,
  AnonymousSessionContextValue,
} from "./AnonymousSessionContext";
import { anonymousSessionState } from "./anonymousSessionState";

const getCachedSession = () =>
  getStorageItemWithFallback<{
    data: AnonymousSession;
    token: string;
  } | null>("session", null);

export const AnonymousSessionProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useSelector(selectAuthState);
  const { aiWorkflowsApi } = useRestApiProvider();
  const interceptorIdRef = useRef<number | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [anonymousSession, setAnonymousSession] =
    useState<AnonymousSession | null>(null);
  const [needsRecaptcha, setNeedsRecaptcha] = useState(false);

  const parsedUA = useMemo(() => UAParser(), []);

  const [createAnonymousSession] =
    aiWorkflowsApi.useCreateAnonymousSessionMutation();

  const getOrCreateAnonymousSessionId = useCallback(() => {
    const sessionId = getStorageItemWithFallback(
      ANONYMOUS_SESSION_ENTITY_ID_KEY,
      getUniqueId()
    );
    return sessionId;
  }, []);

  const setupInterceptor = useCallback((token: string) => {
    if (interceptorIdRef.current !== null) {
      api.interceptors.request.eject(interceptorIdRef.current);
    }

    interceptorIdRef.current = api.interceptors.request.use((config) => {
      const jwtToken = getJwtToken();
      if (jwtToken?.accessToken) {
        return config;
      }
      config.headers = config.headers ?? {};
      config.headers["x-temp-auth-joiint"] = token;
      return config;
    });
  }, []);

  const createSession = useCallback(async (): Promise<boolean> => {
    setIsInitialized(true);
    try {
      const entityId = getOrCreateAnonymousSessionId();

      const storageSession = getCachedSession();

      let session;
      // eslint-disable-next-line no-unsafe-optional-chaining
      if (storageSession && storageSession?.data?.ttl * 1000 > Date.now()) {
        session = storageSession;
      } else {
        // Need ReCAPTCHA for fresh sessions
        setNeedsRecaptcha(true);
        // Wait a tick for ReCAPTCHA to mount
        await new Promise((r) => {
          setTimeout(r, 1000);
        });
        const grcToken = await recaptchaRef.current?.executeAsync();
        if (!grcToken) return false;

        session = await createAnonymousSession({
          unique_id: entityId,
          grc_token: grcToken,
          agent_info: {
            browser: `${parsedUA.browser.name} ${parsedUA.browser.version}`,
            os: `${parsedUA.os.name} ${parsedUA.os.version}`,
            device: parsedUA.device.type ?? "desktop",
          },
        }).unwrap();
      }

      saveItemToStorage("session", session);
      saveItemToStorage(
        ANONYMOUS_SESSION_ENTITY_ID_KEY,
        session.data.entity_id
      );
      setAnonymousSession(session.data);
      anonymousSessionState.setSession(session.token);
      setupInterceptor(session.token);
      setHasSession(true);

      return true;
    } catch (error) {
      logger.error("AnonymousSessionProvider: createSession error", error);

      // Handle 429 — navigate to login with rate limit message
      const axiosError = error as AxiosError;
      if (
        axiosError?.status === 429 ||
        (axiosError as unknown as { data?: { status?: number } })?.data
          ?.status === 429
      ) {
        window.location.href = "/cx/login?rateLimit=true";
        return false;
      }

      return false;
    } finally {
      setNeedsRecaptcha(false);
    }
  }, [
    createAnonymousSession,
    getOrCreateAnonymousSessionId,
    parsedUA.browser.name,
    parsedUA.browser.version,
    parsedUA.device.type,
    parsedUA.os.name,
    parsedUA.os.version,
    setupInterceptor,
  ]);

  // Cleanup when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (interceptorIdRef.current !== null) {
        api.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
      anonymousSessionState.clearSession();
      setAnonymousSession(null);
      setIsInitialized(false);
      setHasSession(false);
    }
  }, [isAuthenticated]);

  const contextValue = useMemo<AnonymousSessionContextValue>(() => {
    if (isAuthenticated) {
      return ANONYMOUS_SESSION_FALLBACK;
    }

    return {
      isAnonymous: isInitialized && !!anonymousSession,
      entityId: anonymousSession?.entity_id || "",
      isInitialized,
      hasSession,
      createSession,
    };
  }, [
    isAuthenticated,
    isInitialized,
    anonymousSession,
    hasSession,
    createSession,
  ]);

  return (
    <AnonymousSessionContext.Provider value={contextValue}>
      {needsRecaptcha && environment.isReCaptchaEnabled && (
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={environment.reCaptchaKey}
        />
      )}
      {children}
    </AnonymousSessionContext.Provider>
  );
};
